import React, { useState, useEffect } from 'react';
import { getInstanceDetails } from '../services/rdsService';

const InstanceDetailsModal = ({ instanceId, onClose, awsCredentials }) => {
  const [instanceDetails, setInstanceDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadInstanceDetails();
  }, [instanceId]);

  const loadInstanceDetails = async () => {
    try {
      setLoading(true);
      console.log(`Loading details for instance: ${instanceId}`);
      const details = await getInstanceDetails(instanceId, awsCredentials);
      setInstanceDetails(details);
      setError(null);
    } catch (err) {
      console.error('Error loading instance details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add toast notification here
    }).catch(() => {
      alert('Failed to copy to clipboard');
    });
  };

  if (loading) {
    return (
      <div className="aws-modal-overlay">
        <div className="aws-modal">
          <div className="aws-modal-header">
            <h2 className="aws-modal-title">Instance Details: {instanceId}</h2>
          </div>
          <div className="aws-modal-body">
            <div className="aws-loading">
              <div className="aws-spinner"></div>
              Loading instance details...
            </div>
          </div>
          <div className="aws-modal-footer">
            <button className="aws-button aws-button-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="aws-modal-overlay">
        <div className="aws-modal">
          <div className="aws-modal-header">
            <h2 className="aws-modal-title">Instance Details: {instanceId}</h2>
          </div>
          <div className="aws-modal-body">
            <div className="aws-alert aws-alert-error">
              <strong>Error loading instance details:</strong> {error}
            </div>
          </div>
          <div className="aws-modal-footer">
            <button className="aws-button aws-button-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="aws-modal-overlay">
      <div className="aws-modal" style={{ width: '800px' }}>
        <div className="aws-modal-header">
          <h2 className="aws-modal-title">Instance Details: {instanceId}</h2>
        </div>
        
        <div className="aws-modal-body">
          {instanceDetails && (
            <div>
              {/* Basic Information */}
              <div className="aws-details-section">
                <h3>Basic Information</h3>
                <div className="aws-details-grid">
                  <div className="aws-details-item">
                    <label>DB Instance Identifier</label>
                    <span>{instanceDetails.DBInstanceIdentifier}</span>
                  </div>
                  <div className="aws-details-item">
                    <label>Status</label>
                    <span className={`aws-status-badge aws-status-${
                      instanceDetails.DBInstanceStatus === 'available' ? 'available' : 'creating'
                    }`}>
                      {instanceDetails.DBInstanceStatus}
                    </span>
                  </div>
                  <div className="aws-details-item">
                    <label>Engine</label>
                    <span>{instanceDetails.Engine} {instanceDetails.EngineVersion}</span>
                  </div>
                  <div className="aws-details-item">
                    <label>DB Instance Class</label>
                    <span>{instanceDetails.DBInstanceClass}</span>
                  </div>
                </div>
              </div>

              {/* Connectivity */}
              <div className="aws-details-section">
                <h3>Connectivity & Security</h3>
                <div className="aws-details-grid">
                  <div className="aws-details-item">
                    <label>Endpoint</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{instanceDetails.Endpoint?.Address || 'Not available'}</span>
                      {instanceDetails.Endpoint?.Address && (
                        <button
                          className="aws-copy-button"
                          onClick={() => copyToClipboard(instanceDetails.Endpoint.Address)}
                          title="Copy endpoint"
                        >
                          📋
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="aws-details-item">
                    <label>Port</label>
                    <span>{instanceDetails.Endpoint?.Port || 'N/A'}</span>
                  </div>
                  <div className="aws-details-item">
                    <label>VPC ID</label>
                    <span>{instanceDetails.DBSubnetGroup?.VpcId || 'N/A'}</span>
                  </div>
                  <div className="aws-details-item">
                    <label>Publicly Accessible</label>
                    <span>{instanceDetails.PubliclyAccessible ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>

              {/* Security Groups */}
              <div className="aws-details-section">
                <h3>Security Groups</h3>
                {instanceDetails.VpcSecurityGroups && instanceDetails.VpcSecurityGroups.length > 0 ? (
                  <div className="aws-security-groups">
                    {instanceDetails.VpcSecurityGroups.map((sg, index) => (
                      <div key={index} className="aws-security-group-item">
                        <div className="aws-security-group-id">
                          <strong>{sg.VpcSecurityGroupId}</strong>
                          <span className={`aws-sg-status ${sg.Status === 'active' ? 'active' : 'inactive'}`}>
                            {sg.Status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="aws-text-muted">No security groups configured</span>
                )}
              </div>

              {/* Subnet Group */}
              {instanceDetails.DBSubnetGroup && (
                <div className="aws-details-section">
                  <h3>Subnet Group</h3>
                  <div className="aws-details-grid">
                    <div className="aws-details-item">
                      <label>DB Subnet Group Name</label>
                      <span>{instanceDetails.DBSubnetGroup.DBSubnetGroupName}</span>
                    </div>
                    <div className="aws-details-item">
                      <label>Description</label>
                      <span>{instanceDetails.DBSubnetGroup.DBSubnetGroupDescription}</span>
                    </div>
                    <div className="aws-details-item">
                      <label>Availability Zones</label>
                      <span>
                        {instanceDetails.DBSubnetGroup.Subnets?.map(subnet => 
                          subnet.SubnetAvailabilityZone?.Name
                        ).filter(Boolean).join(', ') || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Storage & Backup */}
              <div className="aws-details-section">
                <h3>Storage & Backup</h3>
                <div className="aws-details-grid">
                  <div className="aws-details-item">
                    <label>Storage Type</label>
                    <span>{instanceDetails.StorageType || 'N/A'}</span>
                  </div>
                  <div className="aws-details-item">
                    <label>Allocated Storage</label>
                    <span>{instanceDetails.AllocatedStorage ? `${instanceDetails.AllocatedStorage} GB` : 'N/A'}</span>
                  </div>
                  <div className="aws-details-item">
                    <label>Multi-AZ</label>
                    <span>{instanceDetails.MultiAZ ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="aws-details-item">
                    <label>Backup Retention</label>
                    <span>{instanceDetails.BackupRetentionPeriod ? `${instanceDetails.BackupRetentionPeriod} days` : 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div className="aws-details-section">
                <h3>Timestamps</h3>
                <div className="aws-details-grid">
                  <div className="aws-details-item">
                    <label>Instance Create Time</label>
                    <span>
                      {instanceDetails.InstanceCreateTime ? 
                        new Date(instanceDetails.InstanceCreateTime).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                  <div className="aws-details-item">
                    <label>Latest Restorable Time</label>
                    <span>
                      {instanceDetails.LatestRestorableTime ? 
                        new Date(instanceDetails.LatestRestorableTime).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="aws-modal-footer">
          <button className="aws-button aws-button-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstanceDetailsModal;